import React from 'react';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import styles from './InstructionsStep.module.css';

interface InstructionsStepProps {
  onStart: () => void;
}

const evaluationCriteria = [
  {
    name: 'Originalidad y riesgo de la investigación',
    weight: '20%',
    description:
      'Evalúa la novedad, innovación y nivel de riesgo académico del proyecto de investigación propuesto. Considera la originalidad de la hipótesis, metodología y enfoque.',
    aspects: [
      'Novedad e innovación de la hipótesis o pregunta de investigación',
      'Originalidad de la metodología propuesta',
      'Nivel de riesgo académico asumido',
      'Diferenciación respecto al estado del arte',
      'Potencial para generar conocimiento disruptivo',
    ],
    considerations: [
      'Evalúe si el proyecto propone ideas verdaderamente nuevas o enfoques innovadores',
      'Considere el equilibrio entre innovación y viabilidad',
      'Analice si el riesgo asumido está justificado por el potencial impacto',
      'Revise la revisión bibliográfica para validar la originalidad',
    ],
  },
  {
    name: 'Pertinencia nacional e impacto potencial',
    weight: '20%',
    description:
      'Analiza la relevancia del proyecto para el contexto nacional chileno y su potencial impacto en el desarrollo científico, tecnológico, social o económico del país.',
    aspects: [
      'Relevancia para el contexto chileno específico',
      'Potencial impacto en el desarrollo nacional',
      'Alineación con prioridades científicas del país',
      'Contribución al desarrollo social, económico o tecnológico',
      'Aplicabilidad de resultados en el contexto local',
    ],
    considerations: [
      'Evalúe la pertinencia del proyecto para Chile y sus desafíos específicos',
      'Considere el impacto potencial en políticas públicas o desarrollo sectorial',
      'Analice la capacidad del proyecto para abordar necesidades nacionales',
      'Revise la alineación con estrategias de desarrollo nacional',
    ],
  },
  {
    name: 'Escalabilidad global de la idea del proyecto',
    weight: '15%',
    description:
      'Evalúa el potencial del proyecto para expandirse o aplicarse en contextos internacionales, considerando su relevancia y aplicabilidad global.',
    aspects: [
      'Potencial de aplicación internacional',
      'Relevancia para contextos globales similares',
      'Transferibilidad de metodologías y resultados',
      'Contribución al conocimiento global',
      'Escalabilidad de soluciones propuestas',
    ],
    considerations: [
      'Evalúe si los resultados serán aplicables en otros países o contextos',
      'Considere la capacidad de generalización de los hallazgos',
      'Analice el potencial de colaboración internacional futura',
      'Revise la relevancia para comunidades científicas internacionales',
    ],
  },
  {
    name: 'Nivel de colaboración internacional y levantamiento de fondos adicionales',
    weight: '20%',
    description:
      'Considera la capacidad del equipo para establecer colaboraciones internacionales y obtener financiamiento adicional de fuentes externas.',
    aspects: [
      'Capacidad demostrada para colaboraciones internacionales',
      'Redes y contactos internacionales del equipo',
      'Estrategias para obtener financiamiento adicional',
      'Experiencia previa en proyectos internacionales',
      'Potencial para establecer nuevas alianzas',
    ],
    considerations: [
      'Revise el historial de colaboraciones internacionales del equipo',
      'Evalúe las estrategias propuestas para buscar fondos adicionales',
      'Considere la calidad y relevancia de las redes internacionales',
      'Analice la factibilidad de las colaboraciones planteadas',
    ],
  },
  {
    name: 'Viabilidad técnica de la propuesta, planificación',
    weight: '10%',
    description:
      'Evalúa la factibilidad técnica del proyecto, la calidad de la planificación, cronograma y la disponibilidad de recursos necesarios.',
    aspects: [
      'Factibilidad técnica de la metodología propuesta',
      'Disponibilidad y acceso a recursos necesarios',
      'Calidad de la planificación temporal',
      'Identificación y gestión de riesgos',
      'Coherencia entre objetivos, metodología y recursos',
    ],
    considerations: [
      'Evalúe si la metodología es técnicamente realizable',
      'Revise la disponibilidad de equipos, software y recursos humanos',
      'Analice la coherencia del cronograma propuesto',
      'Considere la experiencia del equipo con las técnicas propuestas',
    ],
  },
  {
    name: 'Calidad de los resultados',
    weight: '15%',
    description:
      'Analiza la calidad esperada de los resultados, productos entregables, publicaciones y contribuciones científicas del proyecto.',
    aspects: [
      'Calidad esperada de productos y entregables',
      'Potencial de publicaciones en revistas de alto impacto',
      'Contribución al avance del conocimiento científico',
      'Calidad de la transferencia de conocimiento',
      'Impacto esperado en la comunidad científica',
    ],
    considerations: [
      'Evalúe el potencial de los resultados para generar publicaciones de calidad',
      'Considere la capacidad del equipo para comunicar y difundir resultados',
      'Analice la contribución esperada al avance del campo de estudio',
      'Revise los mecanismos propuestos para la transferencia de conocimiento',
    ],
  },
];

const scaleDescriptions = [
  {
    score: 1,
    label: 'Muy deficiente',
    description: 'Cumplimiento muy bajo de los criterios',
  },
  {
    score: 2,
    label: 'Insuficiente',
    description: 'Cumplimiento parcial e inadecuado',
  },
  {
    score: 3,
    label: 'Aceptable / Suficiente',
    description: 'Cumplimiento básico de los criterios',
  },
  {
    score: 4,
    label: 'Bueno / Sólido',
    description: 'Cumplimiento sólido y bien fundamentado',
  },
  {
    score: 5,
    label: 'Excelente / Ejemplar',
    description: 'Cumplimiento excepcional y destacado',
  },
];

const InstructionsStep: React.FC<InstructionsStepProps> = ({ onStart }) => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Instrucciones de Evaluación</h2>
        <p className={styles.subtitle}>
          A continuación se presentan los criterios y la escala de evaluación
          para la postulación. Lea cuidadosamente antes de comenzar el proceso
          de evaluación.
        </p>
      </div>

      <div className={styles.content}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Criterios de Evaluación</h3>
          <div className={styles.criteriaList}>
            {evaluationCriteria.map((criterion, index) => (
              <div key={index} className={styles.criterionCard}>
                <div className={styles.criterionHeader}>
                  <h4 className={styles.criterionName}>{criterion.name}</h4>
                  <span className={styles.criterionWeight}>
                    {criterion.weight}
                  </span>
                </div>
                <p className={styles.criterionDescription}>
                  {criterion.description}
                </p>
                <div className={styles.criterionDetails}>
                  <div className={styles.detailsBlock}>
                    <h5 className={styles.detailsTitle}>Aspectos a Evaluar</h5>
                    <ul className={styles.detailsList}>
                      {criterion.aspects.map((aspect, i) => (
                        <li key={i}>{aspect}</li>
                      ))}
                    </ul>
                  </div>
                  <div className={styles.detailsBlock}>
                    <h5 className={styles.detailsTitle}>Consideraciones</h5>
                    <ul className={styles.detailsList}>
                      {criterion.considerations.map((c, i) => (
                        <li key={i}>{c}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Consejos de Evaluación</h3>
          <ul className={styles.tipsList}>
            <li>Base su evaluación en evidencia específica de la propuesta</li>
            <li>Use puntuaciones decimales para mayor precisión cuando sea apropiado</li>
            <li>Proporcione comentarios detallados que justifiquen su puntuación</li>
            <li>Mantenga consistencia con otros criterios evaluados</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Escala de Evaluación (1-5)</h3>
          <div className={styles.scaleContainer}>
            {scaleDescriptions.map((scale) => (
              <div key={scale.score} className={styles.scaleItem}>
                <div className={styles.scoreCircle}>{scale.score}</div>
                <div className={styles.scaleContent}>
                  <h4 className={styles.scaleLabel}>{scale.label}</h4>
                  <p className={styles.scaleDescription}>{scale.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Instrucciones Generales</h3>
          <div className={styles.instructionsList}>
            <div className={styles.instruction}>
              <h4>Revisión Detallada</h4>
              <p>
                Revise cuidadosamente todos los documentos y secciones de la
                postulación antes de asignar puntuaciones. La información está
                organizada en secciones expandibles para facilitar la
                navegación.
              </p>
            </div>
            <div className={styles.instruction}>
              <h4>Puntuación y Comentarios</h4>
              <p>
                Para cada criterio, asigne una puntuación de 0 a 5 (se permiten
                decimales) y proporcione comentarios detallados que justifiquen
                su evaluación. Los comentarios son obligatorios para todos los
                criterios.
              </p>
            </div>
            <div className={styles.instruction}>
              <h4>Guardado Automático</h4>
              <p>
                Su evaluación se guardará automáticamente cada 30 segundos
                durante el proceso. Puede pausar y retomar la evaluación en
                cualquier momento antes del envío final.
              </p>
            </div>
            <div className={styles.instruction}>
              <h4>Envío Final</h4>
              <p>
                Una vez completada la evaluación, revise el resumen antes del
                envío final. Una vez enviada, la evaluación no podrá ser
                modificada.
              </p>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Consideraciones Importantes</h3>
          <Alert variant="warning">
            <ul className={styles.warningList}>
              <li>
                <strong>Confidencialidad:</strong> Toda la información contenida
                en la postulación es confidencial y debe ser tratada con la
                máxima discreción.
              </li>
              <li>
                <strong>Objetividad:</strong> Base su evaluación únicamente en
                los méritos académicos y científicos de la propuesta, evitando
                cualquier sesgo personal o conflicto de interés.
              </li>
              <li>
                <strong>Integridad:</strong> Si identifica algún conflicto de
                interés durante la evaluación, declárelo inmediatamente en la
                sección correspondiente.
              </li>
              <li>
                <strong>Plazos:</strong> Complete su evaluación dentro del plazo
                establecido. Las evaluaciones incompletas pueden afectar el
                proceso de selección.
              </li>
            </ul>
          </Alert>
        </section>
      </div>

      <div className={styles.footer}>
        <div className={styles.confirmationBox}>
          <Button
            variant="primary"
            size="default"
            onClick={onStart}
            className={styles.startButton}
          >
            Comenzar Evaluación
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstructionsStep;
